import { useRouter } from "next/router";
import type { NextPage } from "next/types";
import { useEffect, useMemo, useState } from "react";

import { Button, H1, Line, LoadingSpinner, NavTab, P, SplashImage, Tab, toast } from "@sorcery/ui";

import { fromNow } from "@sorcery/utils/universal/time";

import { Page } from "~/components/layouts/Page";
import { UserMetadata } from "~/components/organisms/user/UserMetadata";
import { useUser } from "~/providers/UserProvider";
import { UserTabEnum } from "~/types/user";
import { setupPath, userPath } from "~/utils/paths";
import { api } from "~/utils/trpc";
import { CollectionTab } from "./tabs/CollectionTab";
import { DecksTab } from "./tabs/DecksTab";
import { RenownTab } from "./tabs/RenownTab";
import { ResourcesTab } from "./tabs/ResourcesTab";

const User: NextPage = () => {
  /************************************************************************************
   * initialization
   */
  const { isReady, query: routerParams, push, replace } = useRouter();
  const { user: providerUser } = useUser();

  const targetUserId = routerParams.userId as string | undefined;
  const tab = routerParams.tab as UserTabEnum | undefined;
  const [activeTab, setActiveTab] = useState(tab ?? UserTabEnum.DECKS);

  const showDecksTab = activeTab === UserTabEnum.DECKS;
  const showCollectionTab = activeTab === UserTabEnum.COLLECTION;
  const showRenownTab = activeTab === UserTabEnum.RENOWN;
  const showResourcesTab = activeTab === UserTabEnum.RESOURCES;
  const trpcUtils = api.useUtils();

  /************************************************************************************
   * data fetching
   */
  const { data: extendedUser, isPending: userLoading } = api.user.getById.useQuery(
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    { id: targetUserId! },
    { enabled: isReady },
  );
  const { mutateAsync: toggleFollow, isPending: toggleFollowIsLoading } =
    api.user.toggleFollow.useMutation({
      onSuccess: async () => {
        toast(`User ${followingThisUser ? "unfollowed" : "followed"}`);
        await trpcUtils.user.getById.invalidate({
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          id: targetUserId!,
        });
      },
    });

  /*************************************************************************************************************************
   * react helper hooks
   */
  const isThisUser = !!(extendedUser && providerUser && extendedUser?.id === providerUser?.id);
  const followingThisUser = useMemo(
    () => providerUser && extendedUser?.followers.find((u) => u.id === providerUser.id),
    [providerUser, extendedUser?.followers],
  );
  const isLoading = userLoading;

  // Track tab when router is loaded
  useEffect(() => {
    if (isReady) {
      if (tab && Object.values(UserTabEnum).includes(tab)) {
        setActiveTab(tab);
      } else {
        setActiveTab(UserTabEnum.DECKS);
      }
    }
  }, [tab, isReady]);

  /************************************************************************************
   * render
   */

  return isLoading ? (
    <div className="flex h-screen w-full items-center justify-center">
      <LoadingSpinner />
    </div>
  ) : (
    <Page hero={<SplashImage />}>
      {/* Header */}
      <div className="mb-2 text-center lg:text-left lg:mb-8">
        {/* Header */}
        <div>
          <H1 className="!mb-1 hyphens-auto !text-3xl text-big-text-color lg:!text-4xl">
            @{extendedUser?.username}
          </H1>

          <P className="!mb-0 italic opacity-60">
            <span>Joined&nbsp;</span>
            {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
            {fromNow(extendedUser!.createdAt)}
          </P>

          {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
          <UserMetadata user={extendedUser!} />
          <Line className="my-4 hidden max-w-48 lg:block" />
          {isThisUser ? (
            <Button
              variant="secondary"
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              onClick={() => push(setupPath(userPath(targetUserId!)))}
            >
              <span>Edit Profile</span>
            </Button>
          ) : (
            providerUser && (
              <Button
                disabled={toggleFollowIsLoading}
                variant={followingThisUser ? "destructive" : "default"}
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                onClick={() => toggleFollow({ id: targetUserId! })}
              >
                {followingThisUser ? "Unfollow" : "Follow"}
              </Button>
            )
          )}
        </div>
      </div>

      <Line className="my-6" />

      {/* Content area */}

      {/* Small screen nav */}
      <div className="lg:hidden">
        <div className="flex justify-center">
          {Object.values(UserTabEnum)
            .filter((t) => isThisUser || [UserTabEnum.DECKS, UserTabEnum.RENOWN].includes(t))
            .map((t) => (
              <Tab
                key={t}
                title={t}
                onSelect={() =>
                  void replace(
                    {
                      query: {
                        ...routerParams,
                        tab: t,
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    },
                  )
                }
                active={activeTab === t}
              />
            ))}
        </div>
        <Line className="my-6 block lg:hidden" />
      </div>

      <div className="flex flex-row animate-in fade-in">
        {/* Big screen nav */}
        <div className="mr-6 hidden flex-col border-r border-line-color pr-6 lg:flex">
          <div className="sticky top-6 flex flex-col">
            {Object.values(UserTabEnum)
              .filter((t) => isThisUser || [UserTabEnum.DECKS, UserTabEnum.RENOWN].includes(t))
              .map((t) => (
                <NavTab
                  key={t}
                  title={t}
                  onSelect={() =>
                    void push(
                      {
                        query: {
                          ...routerParams,
                          tab: t,
                        },
                      },
                      undefined,
                      {
                        shallow: true,
                      },
                    )
                  }
                  active={activeTab === t}
                />
              ))}
          </div>
        </div>

        {/* Main section */}

        {showDecksTab && (
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          <DecksTab isThisUser={isThisUser} userId={extendedUser!.id} />
        )}
        {isThisUser && showCollectionTab && <CollectionTab />}
        {/* biome-ignore lint/style/noNonNullAssertion: <explanation> */}
        {showRenownTab && <RenownTab isThisUser={isThisUser} user={extendedUser!} />}
        {isThisUser && showResourcesTab && <ResourcesTab />}
      </div>
    </Page>
  );
};

export default User;
